import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import {
  AccordionDetails,
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  styled,
} from '@mui/material';
import MuiAccordion, { AccordionProps } from '@mui/material/Accordion';
import MuiAccordionSummary, {
  AccordionSummaryProps,
} from '@mui/material/AccordionSummary';
import { SyntheticEvent, memo, useState } from 'react';
import { getGenderedRatingText } from '../../../helpers/ratings';
import { CategoryWithRatings } from '../../../types/category-with-ratings';
import { RatingSentence } from '../../../types/rating-sentence';
import { Student } from '../../../types/student';

const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  '&:not(:last-child)': {
    borderBottom: 0,
  },
  '&::before': {
    display: 'none',
  },
}));

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary
    expandIcon={<ArrowForwardIosIcon sx={{ fontSize: '0.9rem' }} />}
    {...props}
  />
))(({ theme }) => ({
  backgroundColor: 'rgba(0, 0, 0, .03)',
  flexDirection: 'row-reverse',
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(90deg)',
  },
  '& .MuiAccordionSummary-content': {
    marginLeft: theme.spacing(1),
  },
}));

export interface StudentRatingPickerProps {
  categoriesWithRatings: CategoryWithRatings[];
  newSelectedRatingIds: string[];
  toggleRating: (rating: RatingSentence) => void;
  student: Student;
}

interface CategoryAccordionProps {
  categoryWithRatings: CategoryWithRatings;
  selectedRatingIds: string[];
  toggleRating: (rating: RatingSentence) => void;
  onExpandCategory: (expanded: boolean) => void;
  expanded: boolean;
  student: Student;
}

const CategoryAccordion = ({
  categoryWithRatings,
  selectedRatingIds,
  toggleRating,
  onExpandCategory,
  expanded,
  student,
}: CategoryAccordionProps) => {
  const getTitle = () => {
    const name = categoryWithRatings.category.name;
    const ratingsLength = selectedRatingIds.length;

    return ratingsLength === 0 ? name : `${name} (${ratingsLength} vybráno)`;
  };

  return (
    <Accordion
      sx={{ padding: '0px' }}
      key={categoryWithRatings.category.id}
      expanded={expanded}
      onChange={(_: SyntheticEvent, newExpanded: boolean) =>
        onExpandCategory(newExpanded)
      }
    >
      <AccordionSummary>{getTitle()}</AccordionSummary>
      <AccordionDetails sx={{ padding: 0 }}>
        <List disablePadding>
          {categoryWithRatings.ratings.map((rating) => (
            <ListItem disablePadding key={rating.id}>
              <ListItemButton
                sx={{ padding: '4px 6px' }}
                onClick={() => toggleRating(rating)}
              >
                <ListItemIcon sx={{ minWidth: '42px' }}>
                  <Checkbox
                    tabIndex={-1}
                    disableRipple
                    checked={selectedRatingIds.includes(rating.id!.toString())}
                  />
                </ListItemIcon>
                <ListItemText
                  sx={{ margin: '0' }}
                  primaryTypographyProps={{ fontSize: '14px' }}
                  primary={getGenderedRatingText(rating, student.gender)}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </AccordionDetails>
    </Accordion>
  );
};

const CategoryAccordionMemo = memo<CategoryAccordionProps>(
  (props: CategoryAccordionProps) => <CategoryAccordion {...props} />,
  (newProps, oldProps) =>
    newProps.expanded === oldProps.expanded &&
    newProps.selectedRatingIds.length === oldProps.selectedRatingIds.length
);

const StudentRatingPicker = ({
  categoriesWithRatings,
  newSelectedRatingIds,
  toggleRating,
  student,
}: StudentRatingPickerProps) => {
  const [expandedCategoryId, setExpandedCategoryId] = useState<
    string | undefined
  >();

  const handleExpandCategory = (categoryId: string, expanded: boolean) =>
    setExpandedCategoryId(expanded ? categoryId : undefined);

  return (
    <List sx={{ maxHeight: '539px', overflow: 'auto' }} disablePadding>
      {categoriesWithRatings.map((categoryWithRatings) => (
        <CategoryAccordionMemo
          key={categoryWithRatings.category.id}
          categoryWithRatings={categoryWithRatings}
          selectedRatingIds={newSelectedRatingIds.filter((ratingId) =>
            categoryWithRatings.ratings
              .map((rating) => rating.id)
              .includes(ratingId)
          )}
          toggleRating={toggleRating}
          student={student}
          onExpandCategory={(expanded) =>
            handleExpandCategory(
              categoryWithRatings.category.id!.toString(),
              expanded
            )
          }
          expanded={expandedCategoryId === categoryWithRatings.category.id}
        />
      ))}
    </List>
  );
};

export default StudentRatingPicker;
